import axios, { AxiosResponse } from 'axios';
import { setApiAxiosInstance, setAuthAxiosInstance } from '../sic-lib/api/axios';

//if dev & ngrok is running on this machine, those urls will be used
//falling back to localhost if ngrok isnt running.
//otherwise use prod urls
//see configEnv.js in the root
export const WEBHOOK_BASE_URL = (window as any).webhookDomain || 'https://webhook.swsecure.com';
export const AUTH_BASE_URL = (window as any).authDomain || 'https://auth.stayincontact.com';
export const API_BASE_URL = (window as any).apiDomain || 'https://api.stayincontact.com';

console.log('env', process.env.NODE_ENV, API_BASE_URL);

export type WithAccess = {
    access: {
        get: (field: string) => string | undefined;
        set: (field: string, value: string | undefined) => void;
    };
};

function buildWithAccess() {
    const access = new Map<string, string | undefined>();

    access.set('access', undefined);

    const get = (field: string) => access.get(field);
    const set = (field: string, value: string | undefined) => {
        access.set(field, value);
    };

    return (): WithAccess => ({ access: { get, set } });
}

/*
    Use this ONLY if you have a library that cannot make a request through axios instance
    ex: signalR
 */
export const withAccess = buildWithAccess();

function eeAuthConfig({ access }: WithAccess) {
    const instance = axios.create({
        baseURL: '/auth', //dont use the ngrok urls here. all auth is handled via cookie on same domain
    });

    instance.interceptors.request.use(
        function (config) {
            switch (config.url) {
                case '/logout':
                    if (config.headers)
                        config.headers['Authorization'] = `Bearer ${access.get('access')}`;
                    break;
            }

            return config;
        },
        function (error) {
            return Promise.reject(error);
        },
    );

    instance.interceptors.response.use(
        function (response: AxiosResponse<any>): AxiosResponse<any> {
            switch (response.config.url) {
                case '/login':
                case '/refresh':
                    if (response.data.access_token) {
                        access.set('access', response.data.access_token);
                    } else {
                        Promise.reject('Invalid access token');
                    }
                    break;
                case '/logout':
                    access.set('access', undefined);
                    break;
            }

            return response;
        },
        function (error) {
            return Promise.reject(error);
        },
    );

    return instance;
}

export const authInstance = eeAuthConfig(withAccess());
setAuthAxiosInstance(authInstance);

function eeApiConfig({ access }: WithAccess) {
    /* 
        allow only one retry at a time 

        TODO: create logic to handle instance of multiple page requests failing due to error 401
     */
    const retries = 1; // number of allowed retries after a new token is retrieved
    let isRefreshing = false;
    let refreshQueue: any[] = [];

    const instance = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            'Content-Type': 'application/json',
        },
    });

    instance.interceptors.request.use(
        function (config) {
            if (config.headers) config.headers['Authorization'] = `Bearer ${access.get('access')}`;

            return config;
        },
        function (error) {
            return Promise.reject(error);
        },
    );

    instance.interceptors.response.use(
        function (response: AxiosResponse<any>): AxiosResponse<any> {
            return response;
        },
        async function (error) {
            const status = error?.response?.status;
            const orgConfig = error?.config || {};

            if (status != 401) {
                return Promise.reject(error);
            }

            orgConfig._retry = typeof orgConfig._retry === 'undefined' ? 0 : ++orgConfig._retry;

            if (orgConfig._retry >= retries) {
                console.error(
                    '[InterceptorResponse] reached maximum number of retries.',
                    orgConfig.url,
                    orgConfig._retry,
                );
                return Promise.reject(error);
            }

            if (!isRefreshing) {
                isRefreshing = true;

                authInstance
                    .get('/refresh')
                    .then(res => {
                        refreshQueue.forEach(v => v.resolve(res));
                        refreshQueue = [];
                        console.log(
                            '[InterceptorResponse] refreshQueue all pending requests re-attempted.',
                        );
                    })
                    .catch(res => {
                        refreshQueue.forEach(v => v.reject(error));
                        refreshQueue = [];
                        console.error('[InterceptorResponse] refreshQueue all rejected and reset');
                        //revokeAuthentication();
                    })
                    .finally(() => {
                        isRefreshing = false;
                        //Log.log('[InterceptorResponse] refreshAccessToken() finally() called.');
                    });
            }

            //add this request to the queue, handle the collection once refreshAccessToken() finishes
            return new Promise((resolve, reject) => {
                refreshQueue.push({
                    resolve: (res: any) => {
                        //Log.log('refreshQueue.resolve', res, orgConfig); //orgConfig,
                        //update the authorization to re-request
                        const config = {
                            ...orgConfig,
                            headers: {
                                ...orgConfig.headers,
                                Authorization: `Bearer ${res}`,
                            },
                        };
                        resolve(instance.request(config));
                    },
                    reject: (err: any) => {
                        reject(err);
                    },
                });
            });
        },
    );

    return instance;
}

export const apiInstance = eeApiConfig(withAccess());
setApiAxiosInstance(apiInstance);
